/**
 * Preenchimento por CEP nos formulários (ADR-0071) e a regra de UF/CEP que o
 * servidor aplica na ingestão (FORM-36).
 *
 * Funções puras sobre a lista de campos: o FormRenderer decide QUANDO buscar
 * e guarda o estado; aqui fica O QUE a busca preenche e o que o servidor
 * recusaria. O campo é reconhecido pelo destino no lead — `mapeia_para` no
 * builder, `nome_externo` no schema público (`nomeExternoDoCampo` lê os dois),
 * então o preview e a página /f/slug se comportam igual.
 */
import { nomeExternoDoCampo, tipoSemInput } from "../../services/formularios";
import { normalizarCep, normalizarUf } from "../../utils/endereco";
import { chaveDoCampo } from "./condicoes";

/** O que a busca de CEP sabe. Número e complemento, só quem responde. */
export const DESTINOS_DA_BUSCA_DE_CEP = ["logradouro", "bairro", "cidade", "estado"];

export const MSG_CEP_DO_FORMULARIO = "CEP deve ter 8 dígitos.";
export const MSG_UF_DO_FORMULARIO = "Informe a UF: a sigla (ex.: SP) ou o nome do estado.";

const TIPOS_DE_TEXTO = ["TEXTO", "TEXTAREA"];
const TIPOS_DE_ESCOLHA_UNICA = ["SELECT", "RADIO"];

/** Primeiro campo com input mapeado a `destino`, ou null. */
export function campoDoDestino(campos, destino) {
  return (
    (campos || []).find(
      (campo) => !tipoSemInput(campo.tipo) && nomeExternoDoCampo(campo) === destino
    ) || null
  );
}

/** Os 8 dígitos do CEP digitado, ou "" enquanto ainda não é um CEP. */
export function cepCompleto(valor) {
  return normalizarCep(valor) || "";
}

/**
 * Vale buscar? Só com um campo de CEP E algum campo que a busca preenche —
 * sem destino, a busca gastaria requisição (e o limite por IP da rota
 * pública) sem mudar nada na tela.
 */
export function podeBuscarCep(campos) {
  if (!campoDoDestino(campos, "cep")) return false;
  return DESTINOS_DA_BUSCA_DE_CEP.some((destino) => campoDoDestino(campos, destino));
}

function chaveDeOpcao(texto) {
  return String(texto ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

// Numa lista, o valor tem de ser uma opção que o gestor cadastrou: o servidor
// recusa qualquer outro. UF casa pela sigla ("São Paulo" e "SP" são a mesma
// opção); o resto, sem acento nem caixa.
function opcaoCorrespondente(campo, destino, valor) {
  const opcoes = Array.isArray(campo.opcoes) ? campo.opcoes : [];
  const casa =
    destino === "estado"
      ? (opcao) => normalizarUf(opcao) === valor
      : (opcao) => chaveDeOpcao(opcao) === chaveDeOpcao(valor);
  return opcoes.find(casa);
}

function valorParaOCampo(campo, destino, valor) {
  if (TIPOS_DE_TEXTO.includes(campo.tipo)) return valor;
  if (TIPOS_DE_ESCOLHA_UNICA.includes(campo.tipo)) {
    return opcaoCorrespondente(campo, destino, valor);
  }
  return undefined;
}

/**
 * Valores que o endereço encontrado grava no formulário:
 * `{[chaveDoCampo(campo)]: valor}` — o id na página pública, a `_key` no Editor.
 *
 * Só entra o que a busca devolveu preenchido — um CEP geral de cidade não
 * traz rua nem bairro, e isso não pode apagar o que a pessoa já escreveu.
 */
export function valoresDaBuscaDeCep(campos, endereco) {
  return DESTINOS_DA_BUSCA_DE_CEP.reduce((valores, destino) => {
    const campo = campoDoDestino(campos, destino);
    const encontrado = String(endereco?.[destino] ?? "").trim();
    if (!campo || !encontrado) return valores;
    const normalizado = destino === "estado" ? normalizarUf(encontrado) : encontrado;
    if (!normalizado) return valores;
    const valor = valorParaOCampo(campo, destino, normalizado);
    return valor === undefined ? valores : { ...valores, [chaveDoCampo(campo)]: valor };
  }, {});
}

/**
 * Erro de um campo PREENCHIDO mapeado a UF ou CEP, ou "".
 *
 * Mesma régua do servidor (`_uf_ou_recusa`/`_cep_ou_recusa`): UF pela sigla
 * ou pelo nome por extenso, CEP com 8 dígitos e, em lista, vale a primeira
 * opção marcada. Apontar no campo antes de enviar poupa o visitante de um
 * erro genérico no rodapé depois do envio.
 */
export function erroDoCampoDeEndereco(campo, valor) {
  const destino = nomeExternoDoCampo(campo);
  const primeiro = Array.isArray(valor) ? valor[0] : valor;
  if (destino === "estado") return normalizarUf(primeiro) ? "" : MSG_UF_DO_FORMULARIO;
  if (destino === "cep") return normalizarCep(primeiro) ? "" : MSG_CEP_DO_FORMULARIO;
  return "";
}
